import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import {
  Box,
  Paper,
  Typography,
  Button,
  CircularProgress,
  Alert,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
} from '@mui/material';
import Plot from 'react-plotly.js';
import axios from 'axios';

const EVENT_COLORS = {
  regime_change: '#f9a825',
  regime_return: '#7b1fa2',
  true_anomaly: '#d62728',
  sensor_fault: '#b71c1c',
};

const DEFAULT_B27_SENSORS = Array.from({ length: 32 }, (_, i) => `Strain(${i + 1})`);

/** Dev on :3000 often has a broken proxy to :5001; Docker API is on :3001. */
const getApiBase = () => {
  if (process.env.REACT_APP_API_URL) {
    return process.env.REACT_APP_API_URL.replace(/\/$/, '');
  }
  if (typeof window !== 'undefined' && window.location.port === '3000') {
    return `http://${window.location.hostname}:3001/api`;
  }
  return '/api';
};

const apiUrl = (path) => `${getApiBase()}${path.startsWith('/') ? path : `/${path}`}`;

const axiosGetWithRetry = async (url, config, retries = 4) => {
  let lastErr;
  for (let attempt = 0; attempt < retries; attempt += 1) {
    try {
      return await axios.get(url, { ...config, withCredentials: false });
    } catch (err) {
      lastErr = err;
      const status = err.response?.status;
      if (status !== 502 && status !== 503 && status !== 504) {
        throw err;
      }
      await new Promise((r) => { setTimeout(r, 1500 * (attempt + 1)); });
    }
  }
  throw lastErr;
};

const buildMsePlotTraces = (block, labelPrefix, events = []) => {
  if (!block?.mse_scores?.length) {
    return [];
  }
  const flagged = (block.points || []).filter((p) => p.flag);
  const traces = [
    {
      x: block.timestamps,
      y: block.mse_scores,
      type: 'scatter',
      mode: 'lines',
      name: `${labelPrefix} MSE`,
      line: { color: labelPrefix === 'LSTM' ? '#9467bd' : '#1f77b4', width: 1.5 },
    },
    {
      x: block.timestamps,
      y: block.threshold_history,
      type: 'scatter',
      mode: 'lines',
      name: `${labelPrefix} adaptive threshold`,
      line: { color: '#ff7f0e', width: 2, dash: 'dash' },
    },
    {
      x: block.timestamps,
      y: block.mse_scores.map(() => block.initial_threshold),
      type: 'scatter',
      mode: 'lines',
      name: `${labelPrefix} initial threshold`,
      line: { color: '#9e9e9e', width: 1, dash: 'dot' },
    },
    {
      x: flagged.map((p) => p.timestamp),
      y: flagged.map((p) => p.mse),
      type: 'scatter',
      mode: 'markers',
      name: `${labelPrefix} flagged`,
      marker: { color: '#d62728', size: 7, symbol: 'x' },
    },
  ];
  Object.keys(EVENT_COLORS).forEach((eventType) => {
    const evts = events.filter(
      (e) => e.type === eventType && (!e.source || e.source === 'mse' || e.source === 'strain_level'),
    );
    if (evts.length === 0) {
      return;
    }
    traces.push({
      x: evts.map((e) => e.timestamp),
      y: evts.map((e) => e.mse),
      type: 'scatter',
      mode: 'markers',
      name: `${eventType.replace('_', ' ')}`,
      marker: { color: EVENT_COLORS[eventType], size: 11, symbol: 'diamond' },
    });
  });
  return traces;
};

const countCell = (block, key) => {
  if (!block) {
    return '—';
  }
  const aliases = {
    anomaly_points: ['anomaly_points', 'anomaly_point_count', 'raw_anomaly_count'],
    flagged_points: ['flagged_points', 'flagged_point_count', 'adapted_anomaly_count'],
    regime_change_mse: ['regime_change_mse', 'regime_change_count', 'regime_event_count'],
    regime_return: ['regime_return', 'regime_return_count'],
    true_anomaly_points: ['true_anomaly_points', 'true_anomaly_count'],
    sensor_fault_events: ['sensor_fault_events', 'sensor_fault_count'],
  };
  const keys = aliases[key] || [key];
  let v;
  keys.forEach((k) => {
    if (v == null && block[k] != null) {
      v = block[k];
    }
  });
  return v != null ? Number(v).toLocaleString() : '0';
};

const buildTestRegionShapes = (splitTimestamp, endTimestamp) => {
  if (!splitTimestamp) {
    return [];
  }
  return [{
    type: 'rect',
    xref: 'x',
    yref: 'paper',
    x0: splitTimestamp,
    x1: endTimestamp || splitTimestamp,
    y0: 0,
    y1: 1,
    fillcolor: 'rgba(255, 152, 0, 0.12)',
    line: { width: 0 },
  }];
};

const sourceLabel = (ev) => {
  const s = ev?.source || 'mse';
  if (s === 'strain_level') return 'strain';
  if (s === 'feedforward' || s === 'feedforward_train') return 'FF';
  if (s === 'lstm') return 'LSTM';
  return s;
};

const buildStrainEventTraces = (timestamps, yValues, events, baseline = 0) => {
  const yAt = (ev) => {
    if (typeof ev.strain_raw === 'number') {
      return ev.strain_raw;
    }
    const i = timestamps?.indexOf(ev.timestamp) ?? -1;
    if (i >= 0 && yValues?.[i] != null) {
      return yValues[i];
    }
    if (typeof ev.zeroed_strain === 'number') {
      return ev.zeroed_strain + baseline;
    }
    return null;
  };

  const groups = {};
  (events || []).forEach((ev) => {
    const eventType = ev.type;
    if (!EVENT_COLORS[eventType]) {
      return;
    }
    const key = `${eventType}|${sourceLabel(ev)}`;
    if (!groups[key]) {
      groups[key] = [];
    }
    groups[key].push(ev);
  });

  return Object.entries(groups).map(([key, evts]) => {
    const [eventType, src] = key.split('|');
    const xs = [];
    const ys = [];
    evts.forEach((ev) => {
      const y = yAt(ev);
      if (ev.timestamp && y != null) {
        xs.push(ev.timestamp);
        ys.push(y);
      }
    });
    if (xs.length === 0) {
      return null;
    }
    return {
      x: xs,
      y: ys,
      type: 'scatter',
      mode: 'markers',
      name: `${eventType.replace(/_/g, ' ')} (${src})`,
      marker: { color: EVENT_COLORS[eventType], size: 12, symbol: 'diamond' },
    };
  }).filter(Boolean);
};

const buildAnomalyPointTraces = (timestamps, yValues, points) => {
  const tsIndex = new Map((timestamps || []).map((t, i) => [t, i]));
  const xs = [];
  const ys = [];
  (points || []).forEach((p) => {
    if (!p.flag) {
      return;
    }
    if (!['true_anomaly', 'sensor_fault'].includes(p.label)) {
      return;
    }
    const i = tsIndex.get(p.timestamp);
    const y = i != null ? yValues[i] : null;
    if (y != null) {
      xs.push(p.timestamp);
      ys.push(y);
    }
  });
  if (xs.length === 0) {
    return [];
  }
  return [{
    x: xs,
    y: ys,
    type: 'scatter',
    mode: 'markers',
    name: 'anomaly point (test MSE)',
    marker: { color: '#d62728', size: 7, symbol: 'x' },
  }];
};

const DEFAULT_SOURCES = [
  { id: 'b27', label: 'B27 — training folder (B27.csv)' },
  { id: 'dataaaa1', label: 'dataaaa1 — train 2 folder (dataaaa1.csv)' },
  { id: 'm16', label: 'M16 — endpier1 folder (M16.csv)' },
];

const REGIME_SOURCE_IDS = new Set(['b27', 'dataaaa1', 'm16']);
const MUX_SOURCE_IDS = new Set(['b27', 'm16']);

const formatProcessingTime = (seconds) => {
  if (seconds == null || Number.isNaN(Number(seconds))) return '—';
  const s = Number(seconds);
  if (s < 60) return `${s.toFixed(1)} s`;
  const m = Math.floor(s / 60);
  const rem = Math.round(s % 60);
  return rem > 0 ? `${m} min ${rem} s` : `${m} min`;
};

const B27RegimeAdaptation = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [results, setResults] = useState(null);
  const [sourceList, setSourceList] = useState(DEFAULT_SOURCES);
  const [selectedSource, setSelectedSource] = useState('b27');
  const [sensorList, setSensorList] = useState([]);
  const [selectedSensor, setSelectedSensor] = useState('Strain(1)');
  const [jobStatus, setJobStatus] = useState('');
  const [lastProcessingSeconds, setLastProcessingSeconds] = useState(null);
  const [apiOk, setApiOk] = useState(null);

  useEffect(() => {
    const checkApi = async () => {
      try {
        await axios.get(apiUrl('/b27-regime-adaptation/ping'), { timeout: 8000 });
        setApiOk(true);
      } catch {
        setApiOk(false);
      }
    };
    checkApi();
    const fetchSources = async () => {
      try {
        const res = await axiosGetWithRetry(apiUrl('/analysis-files'), { timeout: 30000 });
        const files = (res.data?.files || []).filter((f) => REGIME_SOURCE_IDS.has(f.id));
        if (files.length > 0) {
          setSourceList(files);
          const def = res.data?.default;
          if (def && files.some((f) => f.id === def)) {
            setSelectedSource(def);
          }
        }
      } catch {
        /* keep DEFAULT_SOURCES */
      }
    };
    fetchSources();
  }, []);

  useEffect(() => {
    const fetchSensors = async () => {
      try {
        const res = await axiosGetWithRetry(apiUrl('/b27-regime-sensors'), {
          params: { source: selectedSource },
          timeout: 120000,
        });
        const sensors = res.data?.sensors || [];
        if (sensors.length > 0) {
          setSensorList(sensors);
          setSelectedSensor((prev) => (sensors.includes(prev) ? prev : sensors[0]));
        } else {
          setSensorList(MUX_SOURCE_IDS.has(selectedSource) ? DEFAULT_B27_SENSORS : []);
          setSelectedSensor('Strain(1)');
        }
      } catch {
        setSensorList(MUX_SOURCE_IDS.has(selectedSource) ? DEFAULT_B27_SENSORS : []);
      }
    };
    fetchSensors();
    setResults(null);
  }, [selectedSource]);

  const sleep = (ms) => new Promise((resolve) => {
    setTimeout(resolve, ms);
  });

  const loadData = async () => {
    if (!selectedSensor) {
      setError('Select a sensor first.');
      return;
    }
    setLoading(true);
    setError(null);
    setLastProcessingSeconds(null);
    setJobStatus('Starting…');
    try {
      const startRes = await axiosGetWithRetry(apiUrl('/b27-regime-adaptation/start'), {
        params: { source: selectedSource, sequence_length: 5, sensor: selectedSensor },
        timeout: 120000,
      });
      const jobId = startRes.data?.job_id;
      if (!jobId) {
        setError('Server did not return a job id.');
        return;
      }

      const pollParams = { timeout: 120000 };
      for (let attempt = 0; attempt < 600; attempt += 1) {
        const statusRes = await axiosGetWithRetry(
          apiUrl(`/b27-regime-adaptation/status/${jobId}`),
          pollParams,
          2,
        );
        const { status } = statusRes.data;
        if (status === 'pending' || status === 'running') {
          const elapsed = statusRes.data.elapsed_seconds;
          const elapsedLabel = elapsed != null
            ? formatProcessingTime(elapsed)
            : `${attempt + 1}s`;
          setJobStatus(
            status === 'running'
              ? `Training feedforward + LSTM for ${selectedSensor}… (${elapsedLabel})`
              : 'Queued…',
          );
          await sleep(1000);
          continue;
        }
        if (status === 'complete') {
          const payload = statusRes.data.result;
          if (payload?.error) {
            setError(payload.error);
            return;
          }
          const procSec = statusRes.data.processing_seconds
            ?? payload?.processing_seconds
            ?? payload?.sensors?.[0]?.processing_seconds;
          setLastProcessingSeconds(procSec ?? null);
          setResults(payload);
          setJobStatus('');
          return;
        }
        if (status === 'error') {
          const payload = statusRes.data.result;
          setError(
            statusRes.data.error
            || payload?.error
            || 'Regime analysis job failed.',
          );
          return;
        }
        setError(`Unexpected job status: ${status}`);
        return;
      }
      setError('Job still running after 10 minutes. Try again or pick another sensor.');
    } catch (err) {
      if (err.response?.status === 504) {
        const host = typeof window !== 'undefined' ? window.location.hostname : 'localhost';
        setError(
          `Gateway timeout (504). Open `
          + `http://${host}:3001/b27-regime-adaptation `
          + `(works with Docker). Port 3000 dev proxy is often broken.`,
        );
      } else if (err.code === 'ECONNREFUSED' || !err.response) {
        const host = typeof window !== 'undefined' ? window.location.hostname : 'localhost';
        setError(
          `Cannot reach API at ${getApiBase()}. Run: docker compose up -d `
          + `then open http://${host}:3001/b27-regime-adaptation`,
        );
      } else if (err.response) {
        setError(err.response.data?.error || `Server error: ${err.response.status}`);
      } else {
        setError(err.message || 'Failed to load B27 regime analysis.');
      }
    } finally {
      setLoading(false);
      setJobStatus('');
    }
  };

  const sensorData = (results?.sensors || []).find((s) => s.status === 'ok')
    || (results?.sensors || [])[0]
    || null;

  const processingSeconds = lastProcessingSeconds
    ?? results?.processing_seconds
    ?? sensorData?.processing_seconds;

  const fullTimeline = sensorData?.full_timeline || {
    timestamps: sensorData?.timestamps,
    strain_raw: sensorData?.strain_raw,
    strain_zeroed: sensorData?.strain_zeroed,
  };
  const testHoldout = sensorData?.test || {};
  const splitTs = sensorData?.split_timestamp;
  const fullEndTs = fullTimeline?.timestamps?.length
    ? fullTimeline.timestamps[fullTimeline.timestamps.length - 1]
    : null;
  const testRegionShapes = buildTestRegionShapes(splitTs, fullEndTs);

  const withTestTimestamps = (block) => {
    if (!block) {
      return null;
    }
    return {
      ...block,
      timestamps: testHoldout.timestamps || sensorData?.test_mse_timestamps || block.timestamps,
      mse_scores: testHoldout.mse_scores ?? block.mse_scores,
      threshold_history: testHoldout.threshold_history ?? block.threshold_history,
      points: testHoldout.points ?? block.points,
    };
  };

  const ffModel = withTestTimestamps(sensorData?.models?.feedforward);
  const lstmModel = withTestTimestamps(sensorData?.models?.lstm);
  const primaryEvents = sensorData?.events || [];
  const testPoints = testHoldout.points || sensorData?.points || [];

  const ffMsePlot = ffModel
    ? buildMsePlotTraces(ffModel, 'Feedforward', primaryEvents)
    : [];

  const lstmMsePlot = lstmModel
    ? buildMsePlotTraces(lstmModel, 'LSTM', lstmModel.events || [])
    : [];

  const mergedMse = testHoldout.merged_mse || sensorData?.merged_mse;
  const mergedCounts = sensorData?.counts?.merged;

  const mergedMsePlot = mergedMse?.timestamps?.length
    ? [
      {
        x: mergedMse.timestamps,
        y: mergedMse.feedforward_mse,
        type: 'scatter',
        mode: 'lines',
        name: 'Feedforward MSE',
        line: { color: '#1f77b4', width: 1.5 },
        yaxis: 'y',
      },
      {
        x: mergedMse.timestamps,
        y: mergedMse.lstm_mse,
        type: 'scatter',
        mode: 'lines',
        name: 'LSTM MSE (aligned)',
        line: { color: '#9467bd', width: 1.5 },
        yaxis: 'y2',
      },
      {
        x: mergedMse.timestamps,
        y: mergedMse.timestamps.map(() => mergedMse.feedforward_threshold),
        type: 'scatter',
        mode: 'lines',
        name: 'FF threshold',
        line: { color: '#9e9e9e', width: 1, dash: 'dot' },
        yaxis: 'y',
      },
      {
        x: mergedMse.timestamps,
        y: mergedMse.timestamps.map(() => mergedMse.lstm_threshold),
        type: 'scatter',
        mode: 'lines',
        name: 'LSTM threshold',
        line: { color: '#bcbddc', width: 1, dash: 'dot' },
        yaxis: 'y2',
      },
    ]
    : [];

  const counts = sensorData?.counts || {};
  const ffCounts = counts.feedforward || ffModel;
  const lstmCounts = counts.lstm || lstmModel;

  const originalStrainPlot = sensorData?.status === 'ok' ? [
    {
      x: fullTimeline.timestamps,
      y: fullTimeline.strain_raw,
      type: 'scatter',
      mode: 'lines',
      name: 'Original strain (full timeline)',
      line: { color: '#2ca02c', width: 1.5 },
    },
  ] : [];

  const zeroedStrainPlot = sensorData?.status === 'ok' ? [
    {
      x: fullTimeline.timestamps,
      y: fullTimeline.strain_zeroed ?? fullTimeline.strain_raw,
      type: 'scatter',
      mode: 'lines',
      name: 'Zeroed strain (full timeline)',
      line: { color: '#d62728', width: 1.5 },
    },
  ] : [];

  const testStrainPlot = testHoldout?.timestamps?.length ? [
    {
      x: testHoldout.timestamps,
      y: testHoldout.strain_raw,
      type: 'scatter',
      mode: 'lines',
      name: 'Original strain (20% test)',
      line: { color: '#ff7f0e', width: 2 },
    },
  ] : [];

  const testZeroedPlot = testHoldout?.timestamps?.length ? [
    {
      x: testHoldout.timestamps,
      y: testHoldout.strain_zeroed,
      type: 'scatter',
      mode: 'lines',
      name: 'Zeroed strain (20% test)',
      line: { color: '#e377c2', width: 2 },
    },
  ] : [];

  const rawStrainEventTraces = sensorData?.status === 'ok'
    ? buildStrainEventTraces(
      fullTimeline.timestamps,
      fullTimeline.strain_raw,
      primaryEvents,
      sensorData.baseline ?? 0,
    )
    : [];

  const rawStrainAnomalyTraces = sensorData?.status === 'ok'
    ? buildAnomalyPointTraces(
      fullTimeline.timestamps,
      fullTimeline.strain_raw,
      testPoints,
    )
    : [];

  const ffReconstructedPlot = sensorData?.status === 'ok' && fullTimeline.reconstructed_strain?.length
    ? [{
      x: fullTimeline.reconstructed_strain_timestamps || fullTimeline.timestamps,
      y: fullTimeline.reconstructed_strain,
      type: 'scatter',
      mode: 'lines',
      name: 'FF reconstructed strain',
      line: { color: '#1f77b4', width: 1.5, dash: 'dash' },
    }]
    : [];

  const lstmReconstructedPlot = sensorData?.status === 'ok' && fullTimeline.reconstructed_strain_lstm?.length
    ? [{
      x: fullTimeline.reconstructed_strain_lstm_timestamps || fullTimeline.timestamps,
      y: fullTimeline.reconstructed_strain_lstm,
      type: 'scatter',
      mode: 'lines',
      name: 'LSTM reconstructed strain',
      line: { color: '#9467bd', width: 1.5, dash: 'dot' },
    }]
    : [];

  const zeroedStrainEventTraces = sensorData?.status === 'ok'
    ? buildStrainEventTraces(
      fullTimeline.timestamps,
      fullTimeline.strain_zeroed,
      primaryEvents,
      0,
    )
    : [];

  const zeroedStrainAnomalyTraces = sensorData?.status === 'ok'
    ? buildAnomalyPointTraces(
      fullTimeline.timestamps,
      fullTimeline.strain_zeroed,
      testPoints,
    )
    : [];

  const sensorOptions = sensorList.length > 0
    ? sensorList
    : (results?.sensors || []).map((s) => s.sensor);

  const comparisonRows = [
    { label: 'Anomaly points (MSE > threshold)', ff: 'anomaly_points', lstm: 'anomaly_points' },
    { label: 'Flagged points (after adaptation)', ff: 'flagged_points', lstm: 'flagged_points' },
    { label: 'Regime change (MSE rules only)', ff: 'regime_change_mse', lstm: 'regime_change_mse' },
    { label: 'Regime return events', ff: 'regime_return', lstm: 'regime_return' },
    { label: 'True anomaly points', ff: 'true_anomaly_points', lstm: 'true_anomaly_points' },
    { label: 'Sensor fault events', ff: 'sensor_fault_events', lstm: 'sensor_fault_events' },
  ];

  return (
    <Box sx={{ p: 3, pt: '80px', maxWidth: 1400, mx: 'auto' }}>
      <Paper elevation={2} sx={{ p: 3, mb: 3 }}>
        <Typography variant="h4" gutterBottom>
          B27 Regime Adaptation
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          <strong>80/20 split</strong>: models train on first 80%. Strain charts show the
          <strong> full signal</strong> (green/red) with <strong>20% test</strong> overlaid (orange).
          Shaded band = test window. MSE charts and counts = test holdout only.
          {' '}
          <Link to="/b27-regime-rolling">2 month + 15 day rolling page</Link>
          {' '}
          walks the entire series in calendar blocks.
        </Typography>

        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center', mb: 2 }}>
          <FormControl sx={{ minWidth: 280 }} size="small" required>
            <InputLabel>Data file</InputLabel>
            <Select
              label="Data file"
              value={selectedSource}
              onChange={(e) => setSelectedSource(e.target.value)}
            >
              {sourceList.map((f) => (
                <MenuItem key={f.id} value={f.id}>
                  {f.label || f.id}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl sx={{ minWidth: 220 }} size="small" required>
            <InputLabel>Sensor</InputLabel>
            <Select
              label="Sensor"
              value={selectedSensor}
              onChange={(e) => setSelectedSensor(e.target.value)}
            >
              {sensorOptions.map((s) => (
                <MenuItem key={s} value={s}>{s}</MenuItem>
              ))}
            </Select>
          </FormControl>
          <Button
            variant="contained"
            onClick={loadData}
            disabled={loading || !selectedSensor}
          >
            {loading ? <CircularProgress size={24} /> : `Run ${selectedSensor || 'analysis'}`}
          </Button>
        </Box>

        {loading && (
          <Alert severity="info" sx={{ mb: 2 }}>
            {jobStatus || `Training ${selectedSensor} in the background (safe for Apache/ucii2)…`}
          </Alert>
        )}
        {apiOk === false && (
          <Alert severity="warning" sx={{ mb: 2 }}>
            API not reachable at {getApiBase()}.
            {' '}Use{' '}
            <strong>
              http://{typeof window !== 'undefined' ? window.location.hostname : 'localhost'}:3001/b27-regime-adaptation
            </strong>
            {' '}(Docker). Port 3000 cannot proxy to the backend.
          </Alert>
        )}
        {apiOk === true && typeof window !== 'undefined' && window.location.port === '3000' && (
          <Alert severity="info" sx={{ mb: 2 }}>
            Using API at {getApiBase()} (port 3001) because dev server on 3000 cannot reach the backend.
          </Alert>
        )}
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
      </Paper>

      {sensorData?.status === 'ok' && (
        <>
          <Paper elevation={2} sx={{ p: 2, mb: 3 }}>
            <Typography variant="h6" gutterBottom>
              {sensorData.sensor} — Model comparison
            </Typography>
            <Typography variant="body2" sx={{ mb: 1 }}>
              80/20 split: train
              {' '}
              {sensorData.train_rows?.toLocaleString() ?? sensorData.split_index?.toLocaleString()}
              {' '}
              rows (
              {sensorData.train_points?.toLocaleString()}
              {' '}
              valid strain points) → test
              {' '}
              {sensorData.test_rows?.toLocaleString() ?? sensorData.test_points?.toLocaleString()}
              {' '}
              rows). Strain plots = full + test overlay (
              {testHoldout?.point_count?.toLocaleString() ?? '—'}
              {' '}
              test points,
              {' '}
              {sensorData.test_timestamps?.start}
              {' → '}
              {sensorData.test_timestamps?.end}
              ). MSE counts = test only. Baseline:
              {' '}
              <strong>{sensorData.baseline?.toFixed(4)}</strong>
              {' | '}
              Processing time:
              {' '}
              <strong>{formatProcessingTime(processingSeconds)}</strong>
            </Typography>
            <Table size="small" sx={{ mb: 2 }}>
              <TableHead>
                <TableRow>
                  <TableCell>Metric</TableCell>
                  <TableCell align="right">Feedforward</TableCell>
                  <TableCell align="right">LSTM</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {comparisonRows.map((row) => (
                  <TableRow key={row.label}>
                    <TableCell>{row.label}</TableCell>
                    <TableCell align="right">{countCell(ffCounts, row.ff)}</TableCell>
                    <TableCell align="right">{countCell(lstmCounts, row.lstm)}</TableCell>
                  </TableRow>
                ))}
                <TableRow sx={{ bgcolor: 'action.hover' }}>
                  <TableCell>
                    <strong>Regime change (zeroed-strain level)</strong>
                  </TableCell>
                  <TableCell align="right" colSpan={2}>
                    <strong>
                      {(counts.strain_level_regime_change ?? sensorData.strain_level_regime_count ?? 0).toLocaleString()}
                    </strong>
                    {' '}
                    (applies to both — not from MSE)
                  </TableCell>
                </TableRow>
                <TableRow sx={{ bgcolor: 'action.hover' }}>
                  <TableCell>
                    <strong>Combined regime change (FF + LSTM + strain events)</strong>
                  </TableCell>
                  <TableCell align="right" colSpan={2}>
                    <strong>
                      {(counts.combined_regime_change ?? sensorData.regime_event_count ?? 0).toLocaleString()}
                    </strong>
                  </TableCell>
                </TableRow>
                {mergedCounts && (
                  <>
                    <TableRow>
                      <TableCell colSpan={3}>
                        <Typography variant="subtitle2" sx={{ pt: 1 }}>
                          Merged timeline (OR — either model)
                        </Typography>
                      </TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell>Anomaly if FF or LSTM MSE &gt; threshold</TableCell>
                      <TableCell align="right" colSpan={2}>
                        {(mergedCounts.both_anomaly_points ?? 0).toLocaleString()}
                      </TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell>Flagged if FF or LSTM (after rules)</TableCell>
                      <TableCell align="right" colSpan={2}>
                        {(mergedCounts.both_flagged_points ?? 0).toLocaleString()}
                      </TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell>Both agree: anomaly on FF and LSTM</TableCell>
                      <TableCell align="right" colSpan={2}>
                        {(mergedCounts.both_agree_anomaly_points ?? 0).toLocaleString()}
                      </TableCell>
                    </TableRow>
                  </>
                )}
              </TableBody>
            </Table>
            {ffModel && (
              <Typography variant="caption" color="text.secondary" display="block">
                Feedforward threshold:
                {' '}
                {ffModel.initial_threshold?.toExponential(3)}
                {' → '}
                {ffModel.final_threshold?.toExponential(3)}
                {lstmModel && (
                  <>
                    {' | '}
                    LSTM threshold:
                    {' '}
                    {lstmModel.initial_threshold?.toExponential(3)}
                    {' → '}
                    {lstmModel.final_threshold?.toExponential(3)}
                  </>
                )}
              </Typography>
            )}
          </Paper>

          <Paper elevation={2} sx={{ p: 2, mb: 3 }}>
            <Typography variant="h6" gutterBottom>
              Original strain vs timestamp (full timeline)
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
              Green = full timeline; blue dashed = FF reconstruction; purple dotted = LSTM
              reconstruction (80% train models); orange = 20% test holdout (same values, highlighted).
              Shaded band from split at {splitTs || '80%'}. Diamonds = regime change (strain + MSE).
              Red × = flagged anomalies on test holdout.
            </Typography>
            <Plot
              data={[
                ...originalStrainPlot,
                ...ffReconstructedPlot,
                ...lstmReconstructedPlot,
                ...testStrainPlot,
                ...rawStrainEventTraces,
                ...rawStrainAnomalyTraces,
              ]}
              layout={{
                xaxis: { title: 'Timestamp' },
                yaxis: { title: 'Original strain' },
                height: 400,
                hovermode: 'x unified',
                legend: { orientation: 'h', y: 1.15 },
                shapes: testRegionShapes,
              }}
              style={{ width: '100%' }}
              useResizeHandler
            />
          </Paper>

          <Paper elevation={2} sx={{ p: 2, mb: 3 }}>
            <Typography variant="h6" gutterBottom>
              Zeroed strain vs timestamp (full timeline)
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
              Red = full zeroed series; pink = 20% test overlay. Strain-level regime uses the full series.
            </Typography>
            <Plot
              data={[
                ...zeroedStrainPlot,
                ...testZeroedPlot,
                ...zeroedStrainEventTraces,
                ...zeroedStrainAnomalyTraces,
              ]}
              layout={{
                xaxis: { title: 'Timestamp' },
                yaxis: { title: 'Zeroed strain' },
                height: 400,
                hovermode: 'x unified',
                legend: { orientation: 'h', y: 1.15 },
                shapes: testRegionShapes,
              }}
              style={{ width: '100%' }}
              useResizeHandler
            />
          </Paper>

          {mergedMsePlot.length > 0 && (
            <Paper elevation={2} sx={{ p: 2, mb: 3 }}>
            <Typography variant="h6" gutterBottom>
              Merged MSE comparison (20% test holdout)
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
              Test window only. LSTM MSE aligned onto feedforward timestamps. Left axis = feedforward; right axis = LSTM
                (scales differ — compare shape/timing, not absolute values).
                {' '}
                OR anomaly count:
                {' '}
                <strong>{(mergedCounts?.both_anomaly_points ?? 0).toLocaleString()}</strong>
              </Typography>
              <Plot
                data={mergedMsePlot}
                layout={{
                  xaxis: { title: 'Timestamp' },
                  yaxis: { title: 'Feedforward MSE', side: 'left' },
                  yaxis2: {
                    title: 'LSTM MSE',
                    overlaying: 'y',
                    side: 'right',
                  },
                  height: 440,
                  hovermode: 'x unified',
                  legend: { orientation: 'h', y: 1.2 },
                }}
                style={{ width: '100%' }}
                useResizeHandler
              />
            </Paper>
          )}

          <Paper elevation={2} sx={{ p: 2, mb: 3 }}>
            <Typography variant="h6" gutterBottom>
              Feedforward MSE vs threshold (20% test)
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
              Test holdout only. Anomaly points:
              {' '}
              <strong>{countCell(ffCounts, 'anomaly_points')}</strong>
              {' | '}
              Flagged:
              {' '}
              <strong>{countCell(ffCounts, 'flagged_points')}</strong>
              {' | '}
              Regime change (MSE):
              {' '}
              <strong>{countCell(ffCounts, 'regime_change_mse')}</strong>
            </Typography>
            <Plot
              data={ffMsePlot}
              layout={{
                xaxis: { title: 'Timestamp' },
                yaxis: { title: 'MSE (feedforward scale)' },
                height: 420,
                hovermode: 'x unified',
                legend: { orientation: 'h', y: 1.18 },
              }}
              style={{ width: '100%' }}
              useResizeHandler
            />
          </Paper>

          <Paper elevation={2} sx={{ p: 2, mb: 3 }}>
            <Typography variant="h6" gutterBottom>
              LSTM MSE vs threshold (20% test)
            </Typography>
            {lstmModel && (
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                Anomaly points:
                {' '}
                <strong>{countCell(lstmCounts, 'anomaly_points')}</strong>
                {' | '}
                Flagged:
                {' '}
                <strong>{countCell(lstmCounts, 'flagged_points')}</strong>
                {' | '}
                Regime change (MSE):
                {' '}
                <strong>{countCell(lstmCounts, 'regime_change_mse')}</strong>
              </Typography>
            )}
            {!lstmModel && (
              <Alert severity="warning" sx={{ mb: 2 }}>
                LSTM results unavailable for this sensor (insufficient data or training failed).
              </Alert>
            )}
            {lstmModel && (
              <Plot
                data={lstmMsePlot}
                layout={{
                  xaxis: { title: 'Timestamp' },
                  yaxis: { title: 'MSE (LSTM scale — not comparable to feedforward)' },
                  height: 420,
                  hovermode: 'x unified',
                  legend: { orientation: 'h', y: 1.18 },
                }}
                style={{ width: '100%' }}
                useResizeHandler
              />
            )}
          </Paper>

          {(sensorData.events || []).length > 0 && (
            <Paper elevation={2} sx={{ p: 2 }}>
              <Typography variant="h6" gutterBottom>
                Change-point events
              </Typography>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Type</TableCell>
                    <TableCell>Source</TableCell>
                    <TableCell>Timestamp</TableCell>
                    <TableCell align="right">Detail</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {sensorData.events.map((ev, i) => (
                    <TableRow key={`${ev.type}-${ev.index}-${i}`}>
                      <TableCell>{ev.type}</TableCell>
                      <TableCell>{ev.source || 'feedforward'}</TableCell>
                      <TableCell>{ev.timestamp}</TableCell>
                      <TableCell align="right">
                        {ev.source === 'strain_level' && typeof ev.window_shift === 'number'
                          ? `shift ${ev.window_shift.toFixed(1)}`
                          : (typeof ev.mse === 'number' ? ev.mse.toExponential(3) : '—')}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Paper>
          )}
        </>
      )}

      {results && sensorData?.status !== 'ok' && (
        <Alert severity="warning">
          {sensorData?.reason || 'Analysis did not complete for this sensor.'}
        </Alert>
      )}
    </Box>
  );
};

export default B27RegimeAdaptation;
